/**
 * Ingresos Page — Card-based UI
 * src/pages/Ingresos.jsx
 *
 * Shows income as card list. Card: person color bar, tipo, bruto, impuestos (red), neto (green).
 * Modal: Person select, Tipo select, Bruto input, Impuestos input, computed Neto shown read-only.
 * Seeds data on first load if collection is empty.
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { showToast } from '../components/Toast';
import { useSettings } from '../context/SettingsContext';
import { logActivity } from '../utils/auditLog';

const fmt = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(amount || 0);

const TIPO_INGRESOS = ['Nomina', 'Inversion', 'Bonos', 'Prima Vacacional', 'Otros', 'CETES'];

const SEED_INGRESOS = [
  { person: 'Tumi',    tipo: 'Nomina', bruto: 222486, impuestos: 64415.60 },
  { person: 'Alberto', tipo: 'Nomina', bruto: 195000, impuestos: 0        },
];

async function seedIngresos(db) {
  const snap = await getDocs(collection(db, 'ingresos'));
  if (snap.empty) {
    for (const item of SEED_INGRESOS) {
      await addDoc(collection(db, 'ingresos'), item);
    }
    const newSnap = await getDocs(collection(db, 'ingresos'));
    return newSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

const personColor = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

const BLANK_MODAL = {
  id: null, person: 'Tumi', tipo: 'Nomina', bruto: 0, impuestos: 0, saving: false,
};

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

export default function Ingresos({ db, user }) {
  const { t } = useSettings();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await seedIngresos(db);
        setRows(data);
      } catch (err) {
        console.error('Error loading ingresos:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [db]);

  const totalBruto     = rows.reduce((s, r) => s + (parseFloat(r.bruto) || 0), 0);
  const totalImpuestos = rows.reduce((s, r) => s + (parseFloat(r.impuestos) || 0), 0);
  const totalNeto      = totalBruto - totalImpuestos;

  const openAdd  = () => setModal({ ...BLANK_MODAL });
  const openEdit = (row) => setModal({
    id: row.id, person: row.person, tipo: row.tipo || 'Nomina',
    bruto: row.bruto || 0, impuestos: row.impuestos || 0, saving: false,
  });

  const handleSave = async () => {
    if (!modal) return;
    setModal(m => ({ ...m, saving: true }));
    try {
      const data = {
        person:    modal.person,
        tipo:      modal.tipo,
        bruto:     parseFloat(modal.bruto) || 0,
        impuestos: parseFloat(modal.impuestos) || 0,
      };
      if (modal.id) {
        await updateDoc(doc(db, 'ingresos', modal.id), data);
        setRows(prev => prev.map(r => r.id === modal.id ? { ...r, ...data } : r));
        await logActivity(db, user, 'editar', 'Ingreso', { persona: modal.person, tipo: modal.tipo, bruto: modal.bruto, impuestos: modal.impuestos });
        showToast('Ingreso actualizado');
      } else {
        const ref = await addDoc(collection(db, 'ingresos'), data);
        setRows(prev => [...prev, { id: ref.id, ...data }]);
        await logActivity(db, user, 'crear', 'Ingreso', { persona: modal.person, tipo: modal.tipo, bruto: modal.bruto, impuestos: modal.impuestos });
        showToast('Ingreso agregado');
      }
      setModal(null);
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setModal(m => ({ ...m, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!modal?.id) return;
    try {
      await deleteDoc(doc(db, 'ingresos', modal.id));
      setRows(prev => prev.filter(r => r.id !== modal.id));
      await logActivity(db, user, 'eliminar', 'Ingreso', { persona: modal.person, tipo: modal.tipo, monto: modal.bruto });
      setModal(null);
      showToast('Ingreso eliminado');
    } catch (err) {
      console.error(err);
      showToast('Error al eliminar', 'error');
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
      </div>
    );
  }

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  return (
    <div className="page-container">
      {/* Title + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t('ingresos')} <span style={mxnBadge}>MXN</span></h1>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#1a472a', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 0.875rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          <Plus size={16} /> {t('add')}
        </button>
      </div>

      {/* Summary cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-card-label">Total Ingresos Neto</div>
          <div className="summary-card-value" style={{ color: '#10b981' }}>{fmt(totalNeto)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('bruto')}</div>
          <div className="summary-card-value">{fmt(totalBruto)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('impuestos')}</div>
          <div className="summary-card-value" style={{ color: '#ef4444' }}>-{fmt(totalImpuestos)}</div>
        </div>
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Sin ingresos. Agrega uno con el boton +.
          </div>
        )}

        {rows.map(row => {
          const neto = (parseFloat(row.bruto) || 0) - (parseFloat(row.impuestos) || 0);
          return (
            <div key={row.id} onClick={() => openEdit(row)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: '1px solid var(--border-color)', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}>
              {/* Person color bar */}
              <div style={{ width: 4, height: 52, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />

              {/* Center info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person) }}>{row.person}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', background: 'var(--bg-tertiary)', borderRadius: '0.25rem', padding: '0.05rem 0.4rem' }}>{row.tipo}</span>
                </div>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.15rem' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {t('bruto')}: <strong style={{ color: 'var(--text-primary)' }}>{fmt(row.bruto)}</strong>
                  </span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    {t('impuestos')}: <strong style={{ color: '#ef4444' }}>-{fmt(row.impuestos)}</strong>
                  </span>
                </div>
              </div>

              {/* Right: neto */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.1rem' }}>{t('neto')}</div>
                <div style={{ fontSize: '1rem', fontWeight: 700, color: '#10b981' }}>{fmt(neto)}</div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              {modal.id ? t('edit') : t('add')} Ingreso
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>{t('person')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${modal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: modal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo */}
              <div>
                <label style={labelStyle}>{t('tipo')}</label>
                <select value={modal.tipo} onChange={e => setModal(m => ({ ...m, tipo: e.target.value }))} style={inputStyle}>
                  {TIPO_INGRESOS.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </div>

              {/* Bruto */}
              <div>
                <label style={labelStyle}>{t('bruto')}</label>
                <input type="number" inputMode="decimal" value={modal.bruto} onChange={e => setModal(m => ({ ...m, bruto: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>

              {/* Impuestos */}
              <div>
                <label style={labelStyle}>{t('impuestos')}</label>
                <input type="number" inputMode="decimal" value={modal.impuestos} onChange={e => setModal(m => ({ ...m, impuestos: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right', color: '#ef4444' }} />
              </div>

              {/* Neto (read-only) */}
              <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '0.5rem', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t('neto')}</span>
                <span style={{ fontSize: '1.1rem', fontWeight: 700, color: '#10b981' }}>
                  {fmt((parseFloat(modal.bruto) || 0) - (parseFloat(modal.impuestos) || 0))}
                </span>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {modal.id && (
                <button onClick={handleDelete}
                  style={{ padding: '0.7rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Trash2 size={15} />{t('delete')}
                </button>
              )}
              <button onClick={() => setModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                {t('cancel')}
              </button>
              <button onClick={handleSave} disabled={modal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: modal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: modal.saving ? 0.7 : 1 }}>
                {modal.saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
